import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  Legend, ReferenceLine, Cell,
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Styled Components
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ZakupyByRegionChart = ({ purchaseData = [], clientsData = [] }) => {
  const [regionTransactionData, setRegionTransactionData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [avgCurrent, setAvgCurrent] = useState(0);

  // Color scheme
  const COLOR_CURRENT = "#9848c7"; // purple (current year)
  const COLOR_PREV    = "#FF3B30"; // red (previous year)
  const COLOR_MAX     = "#6e2d9c"; // darker purple for max highlight
  const COLOR_AVG     = "#1E90FF"; // blue (average line)

  // Mapping (normalize client wojewodztwo names -> display names)
  const regionNameMapping = {
    MAZOWIECKIE: "mazowieckie", ŚLĄSKIE: "śląskie", PODKARPACKIE: "podkarpackie",
    LUBUSKIE: "lubuskie", ZACHODNIOPOMORSKIE: "zachodniopomorskie",
    WIELKOPOLSKIE: "wielkopolskie", DOLNOŚLĄSKIE: "dolnośląskie", ŁÓDZKIE: "łódzkie",
    ŚWIĘTOKRZYSKIE: "świętokrzyskie", POMORSKIE: "pomorskie", LUBELSKIE: "lubelskie",
    OPOLSKIE: "opolskie", PODLASKIE: "podlaskie", "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
    "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie", MAŁOPOLSKIE: "małopolskie",
  };

  // Init default filters
  useEffect(() => {
    if (!purchaseData.length || !clientsData.length) return;
    const years = [...new Set(purchaseData.map(r => r.Rok).filter(y => typeof y === "number"))].sort((a, b) => b - a);
    setSelectedYear(years[0] || null);
    setSelectedMonth(0); // "Cały rok"
  }, [purchaseData, clientsData]);

  // Build chart data whenever filters change
  useEffect(() => {
    if (!selectedYear) return;
    const includeFullYear = selectedMonth === 0;

    // NIP -> region map
    const nipToRegion = {};
    clientsData.forEach(c => {
      const nip = c.nip?.trim();
      const regRaw = c.wojewodztwo?.trim().toUpperCase();
      if (nip) nipToRegion[nip] = regionNameMapping[regRaw] || "Unknown";
    });

    const filterPeriod = (year) =>
      purchaseData.filter(row => {
        const rok = row.Rok;
        const miesiac = row.Miesiac;
        return rok === year && (includeFullYear || miesiac === selectedMonth);
      });

    const currentRows = filterPeriod(selectedYear);
    const prevRows = filterPeriod(selectedYear - 1);

    const countByRegion = (rows) =>
      rows.reduce((acc, row) => {
        const nip = row.NrDostawcy?.trim();
        const region = nipToRegion[nip] || "Unknown";
        if (region !== "Unknown") acc[region] = (acc[region] || 0) + 1;
        return acc;
      }, {});

    const curCounts = countByRegion(currentRows);
    const prevCounts = countByRegion(prevRows);

    const allRegions = Array.from(new Set([...Object.keys(curCounts), ...Object.keys(prevCounts)]));

    const data = allRegions.map(region => ({
      region,
      current: curCounts[region] || 0,
      prevYear: prevCounts[region] || 0,
    }));

    const maxCurrent = data.length ? Math.max(...data.map(d => d.current)) : 0;
    const avg = data.length ? data.reduce((s, d) => s + d.current, 0) / data.length : 0;

    setAvgCurrent(avg);
    setRegionTransactionData(
      data
        .map(d => ({ ...d, isMax: d.current === maxCurrent && maxCurrent > 0 }))
        .sort((a, b) => b.current - a.current) // optional: sort by current desc
    );
  }, [purchaseData, clientsData, selectedYear, selectedMonth]);

  const getMonthName = (m) => new Date(2000, m - 1).toLocaleString("pl-PL", { month: "long" });

  const availableYears = [...new Set(
    purchaseData.map(r => r.Rok).filter(y => typeof y === "number")
  )].sort((a, b) => b - a);

  const availableMonths = [...new Set(
    purchaseData.filter(r => r.Rok === selectedYear)
      .map(r => r.Miesiac)
      .filter(m => typeof m === "number")
  )].sort((a, b) => a - b);

  const prevYearLabel = selectedYear ? `r-1 (${selectedYear - 1})` : "r-1";

  return (
    <div style={{ margin: "20px auto", width: "90%" }}>
      <ChartTitle>
        Liczba Zakupów według Województw
        <InfoIconWrapper data-tooltip-id="zakupy-region-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="zakupy-region-tooltip"
        place="left"
        content={
          <>
            <strong>Liczba zakupów</strong><br />
            Wykres pokazuje liczbę zakupów wg województw,<br />
            filtrowane po roku i miesiącu, z porównaniem r/r.
          </>
        }
      />

      <Controls>
        <div>
          <label>Rok: </label>
          <Select value={selectedYear || ""} onChange={e => setSelectedYear(Number(e.target.value))}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>
        <div>
          <label>Miesiąc: </label>
          <Select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
            <option value={0}>Cały rok</option>
            {availableMonths.map(m => (
              <option key={m} value={m}>{getMonthName(m)}</option>
            ))}
          </Select>
        </div>
      </Controls>

      <ResponsiveContainer width="100%" height={520}>
        <BarChart data={regionTransactionData} layout="vertical" margin={{ left: 8, right: 24 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" tickFormatter={(val) => val.toLocaleString()} />
          <YAxis type="category" dataKey="region" width={170} />

          <Tooltip
            formatter={(val, key) => {
              const label = key === "current" ? "Bieżący okres" : "r-1";
              return [`${val.toLocaleString()} zakupów`, label];
            }}
          />

          {/* Force legend colors to match our scheme */}
          <Legend
            payload={[
              { value: `${selectedYear || ""}`, type: "square", color: COLOR_CURRENT, id: "legend-current" },
              { value: prevYearLabel, type: "square", color: COLOR_PREV, id: "legend-prev" },
              { value: "Średnia (bieżący)", type: "line", color: COLOR_AVG, id: "legend-avg" },
            ]}
          />

          {/* Average line (blue) */}
          <ReferenceLine
            x={avgCurrent}
            stroke={COLOR_AVG}
            strokeDasharray="4 4"
            label={{ value: "Średnia (bieżący)", position: "top", fill: COLOR_AVG, fontSize: 12 }}
          />

          {/* Previous year (red) */}
          <Bar
            dataKey="prevYear"
            name={prevYearLabel}
            fill={COLOR_PREV}
            barSize={26}
          />

          {/* Current year (purple) with max highlighted in darker purple */}
          <Bar
            dataKey="current"
            name={`${selectedYear || ""}`}
            fill={COLOR_CURRENT}
            barSize={26}
          >
            {regionTransactionData.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={entry.isMax ? COLOR_MAX : COLOR_CURRENT} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ZakupyByRegionChart;
